import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Loader2 } from 'lucide-react';
import { contactInfo } from '../data/mock';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const CONTACT_ENDPOINT = `${API_BASE}/api/contact`;

const ContactCard = ({ icon: Icon, title, subtitle, children }) => (
  <div className="bg-slate-800/50 rounded-xl p-6 hover:bg-slate-800/70 transition-colors">
    <div className="w-14 h-14 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-5">
      <Icon className="w-7 h-7 text-cyan-400" />
    </div>
    <h3 className="text-2xl font-bold text-white mb-1">{title}</h3>
    <p className="text-gray-500 mb-4">{subtitle}</p>
    {children}
  </div>
);

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(CONTACT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message
        })
      });

      if (response.ok) {
        // GA4 conversion event - fires on successful submission
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
          window.gtag('event', 'quote_submitted', {
            event_category: 'engagement',
            event_label: 'contact_form',
            form_location: 'contact_section',
          });
        }
        setSubmitted(true);
        setFormData({ name: '', phone: '', email: '', message: '' });
        setTimeout(() => setSubmitted(false), 5000);
      } else {
        let errMsg = 'Something went wrong. Please try again.';
        try {
          const data = await response.json();
          errMsg = data.detail || data.error || errMsg;
        } catch {}
        setError(errMsg);
      }
    } catch (err) {
      setError('Failed to send message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-slate-800 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get in Touch
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Ready to transform your vehicle? Contact us for a free quote
          </p>
        </div>

        {/* Contact Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {/* Phone */}
          <ContactCard icon={Phone} title="Phone" subtitle="Give us a call">
            <a 
              href={`tel:${contactInfo.phone}`}
              className="text-xl text-white hover:text-cyan-400 transition-colors font-medium"
            >
              {contactInfo.phone}
            </a>
          </ContactCard>

          {/* Email */}
          <ContactCard icon={Mail} title="Email" subtitle="Send us a message">
            <a 
              href={`mailto:${contactInfo.email}`}
              className="text-lg text-white hover:text-cyan-400 transition-colors font-medium whitespace-nowrap"
            >
              {contactInfo.email}
            </a>
          </ContactCard>

          {/* Location */}
          <ContactCard icon={MapPin} title="Location" subtitle="Visit our shop">
            <address className="not-italic text-xl text-white">
              {contactInfo.address.street}<br />
              {contactInfo.address.city}
            </address>
          </ContactCard>
        </div>

        {/* Contact Form */}
        <div className="max-w-2xl mx-auto">
          <div className="bg-slate-700/50 rounded-2xl p-8 md:p-10">
            <h3 className="text-2xl font-bold text-white mb-2">
              Request a Quote
            </h3>
            <p className="text-gray-400 mb-8">
              Fill out the form below and we'll get back to you soon
            </p>

            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-cyan-400" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Thank you!</h4>
                <p className="text-gray-400">We'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="bg-red-500/20 border border-red-500/50 text-red-300 px-4 py-3 rounded-lg">
                    {error}
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-white font-medium mb-2">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      required
                      className="w-full bg-slate-600/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-white font-medium mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Your phone"
                      className="w-full bg-slate-600/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your email"
                    required
                    className="w-full bg-slate-600/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-white font-medium mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your project..."
                    rows={5}
                    required
                    className="w-full bg-slate-600/50 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-cyan-500/50 disabled:cursor-not-allowed text-white px-8 py-4 rounded-lg font-semibold transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-cyan-500/25"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Check = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

export default Contact;
